import { DEALER_DEFAULT_VALUE } from '@/app/components/sections/dealer/utils/dealer-constant';
import { formatTel } from '@/app/utils/format';
import type { DealerTel } from '@/services/contentstack/models/dealer';

export interface GroupedPhone {
  stripDash: string;
  displayBase: string;
  exts: string[];
}

const isDialableBase = (stripDash: string) =>
  stripDash.length >= 8 && stripDash !== '0';

const digitsOnly = (value: string) => value.replace(/[^\d]/g, '');

const pushUniqueExt = (group: GroupedPhone, ext: string) => {
  if (ext && !group.exts.includes(ext)) {
    group.exts.push(ext);
  }
};

/**
 * Full to-number range suffix — only when from < to and they differ within the last 3 digits.
 * e.g. "025130310" + "025130313" -> "13"
 * e.g. "024821691" + "024821710" -> "710"
 * e.g. "038612254" + "038611189" -> null (diff starts before last 3)
 * e.g. "034816189" + "034816152" -> null (from > to)
 */
const getPhoneRangeSuffix = (
  fromDigits: string,
  toDigits: string
): string | null => {
  if (
    !fromDigits ||
    !toDigits ||
    fromDigits === toDigits ||
    fromDigits.length !== toDigits.length ||
    fromDigits >= toDigits
  ) {
    return null;
  }

  let firstDiff = 0;
  while (
    firstDiff < fromDigits.length &&
    fromDigits[firstDiff] === toDigits[firstDiff]
  ) {
    firstDiff += 1;
  }

  // Only range-format when the first difference is within the last 3 digits
  const lastThreeStart = fromDigits.length - 3;
  if (firstDiff < lastThreeStart) return null;

  // Show at least 2 end digits so "…310" / "…313" becomes "… - 13"
  const minSuffixLen = 2;
  const suffixStart = Math.min(
    firstDiff,
    Math.max(0, fromDigits.length - minSuffixLen)
  );
  const suffix = toDigits.slice(suffixStart);

  if (suffix.length === 0) return null;

  return suffix;
};

export const groupDealerTels = (array: DealerTel[]): GroupedPhone[] => {
  const groups: GroupedPhone[] = [];

  for (const item of array) {
    const rawTel = item.tel ?? '';
    const stripDash = formatTel(rawTel, true);
    const ext = item.ext?.trim() ?? '';
    const previous = groups[groups.length - 1];

    // Short TO values (e.g. "904", "6") append as a range end — use raw
    // digits so formatTel does not prepend a leading 0
    if (previous && !isDialableBase(stripDash)) {
      const rangeSuffix = digitsOnly(rawTel);
      if (rangeSuffix && rangeSuffix !== '0') {
        previous.displayBase = `${previous.displayBase} - ${rangeSuffix}`;
      }
      pushUniqueExt(previous, ext);
      continue;
    }

    const rangeSuffix =
      previous && isDialableBase(stripDash)
        ? getPhoneRangeSuffix(previous.stripDash, stripDash)
        : null;

    if (previous && rangeSuffix) {
      previous.displayBase = `${previous.displayBase} - ${rangeSuffix}`;
      pushUniqueExt(previous, ext);
      continue;
    }

    const existing = groups.find((g) => g.stripDash === stripDash);
    if (existing) {
      pushUniqueExt(existing, ext);
      continue;
    }

    groups.push({
      stripDash,
      displayBase: formatTel(rawTel),
      exts: ext ? [ext] : [],
    });
  }

  return groups;
};

export const createDealerNameFormat = (name?: string) => {
  const dealerName = name || DEALER_DEFAULT_VALUE.NAME;
  const splitDealerNameArrays = dealerName.split(' ').map((item) => {
    if (item.includes('((')) {
      item = item.replace('((', '<span>(');
    } else {
      item = item.replace('(', '<span>(');
    }

    if (item.includes('))')) {
      item = item.replace('))', '))</span>');
    } else {
      item = item.replace(')', ')</span>');
    }
    return item;
  });

  return splitDealerNameArrays.join(' ');
};
